import React from "react";
import { useDelete } from "../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import t from "../../../../i18n";
import ModalForm from "@ui/Modal/Form";
import Text from "@ui/Text";
import { css } from "@linaria/core";
import { spacings } from "@ui/Theme";
import AnalysesSelectionSummary from "./AnalysesSelectionSummary";
import SelectionRow from "./SelectionRow";
import { analysesRatioFormat } from "./utils";
import cn from "classnames";
import { SmallInput } from "./SmallInput";
import { Hr } from "./DesignComponents";

interface Props {
  closeModal: () => void;
  from: Date;
  to: Date;
  projectId: string;
  activeAnalysesCount: number;
  dataPointsCount: number;
}

const DeactivateAnalyseModalForm = ({
  closeModal,
  from,
  to,
  projectId,
  activeAnalysesCount,
  dataPointsCount,
}: Props) => {
  const deactivateRange = useDelete("/projects/:projectId(uuid)/analyses/activation/range");
  const [analysesToDeactivateFromInput, setanalysesToDeactivateFromInput] = React.useState("");
  const { addToast } = useToasts();

  const analysesToDeactivate = analysesToDeactivateFromInput
    ? parseInt(analysesToDeactivateFromInput)
    : 0;
  const targetCount = activeAnalysesCount - analysesToDeactivate;
  const timeWindowInMS = Math.abs(to.getTime() - from.getTime());
  const errorToManyAnalysesDeactivated = activeAnalysesCount - analysesToDeactivate < 0;

  const handleSubmit = async () => {
    if (analysesToDeactivate === 0 || errorToManyAnalysesDeactivated) return false;

    await deactivateRange({
      query: { from, to, amount: analysesToDeactivate },
      routeParams: { projectId },
    });

    // Re-using add/remove success requires a negative for removal
    addToast(
      "success",
      t("measures.chart.selected.analyses.select.range.success", analysesToDeactivate * -1),
    );
    return true;
  };

  return (
    <ModalForm
      className={modalFormCss}
      cancellable
      onRequestClose={closeModal}
      onSubmit={
        analysesToDeactivateFromInput === "" ||
        analysesToDeactivate <= 0 ||
        errorToManyAnalysesDeactivated
          ? false
          : handleSubmit
      }
      okButtonVariant="destructive"
      okButtonName={t("commons.button.deactivate")}
    >
      <>
        <AnalysesSelectionSummary
          total={activeAnalysesCount}
          totalPossible={dataPointsCount}
          timeWindowInMS={timeWindowInMS}
        />

        <Hr />

        <Text className={marginBottomM}>
          {t("measures.chart.selected.analyses.modal.deactivate.information.erase_count")}
        </Text>

        <div className={marginBottomM}>
          <SmallInput
            className={cn(smallInputCss)}
            type="number"
            value={
              analysesToDeactivate === 0 || analysesToDeactivate <= 0 ? "" : analysesToDeactivate
            }
            onChange={e => setanalysesToDeactivateFromInput(e.target.value)}
            error={
              errorToManyAnalysesDeactivated &&
              t("measures.chart.selected.analyses.modal.deactivate.form.errors.erase_count", {
                count: activeAnalysesCount,
              })
            }
            label={t("measures.chart.selected.analyses.modal.deactivate.form.labels.erase_count")}
          />
        </div>

        {!errorToManyAnalysesDeactivated && analysesToDeactivate > 0 && (
          <>
            <SelectionRow
              label={t(
                "measures.chart.selected.analyses.modal.deactivate.information.remaining_analyses",
              )}
              value={targetCount}
            />

            <SelectionRow
              label={t(
                "measures.chart.selected.analyses.modal.deactivate.information.resulting_frequency",
              )}
              value={analysesRatioFormat(targetCount, timeWindowInMS)}
            />
          </>
        )}
      </>
    </ModalForm>
  );
};

const modalFormCss = css`
  row-gap: 0;
`;
const smallInputCss = css`
  max-width: 110px;
`;

const marginBottomM = css`
  margin-bottom: ${spacings.m};
`;

export default DeactivateAnalyseModalForm;
